import type { Pendo } from '#shared/pendo'
import { datadogRum } from '@datadog/browser-rum'

const MAX_PENDING_TRACK_EVENTS = 50
const TRACK_FLUSH_INTERVAL_MS = 250
const MAX_TRACK_EVENT_AGE_MS = 10_000

interface PendingTrackEvent {
  eventName: string
  metadata?: Record<string, any>
  queuedAt: number
  resolve: (delivered: boolean) => void
}

const pendingTrackEvents: PendingTrackEvent[] = []
let trackFlushTimer: ReturnType<typeof setTimeout> | null = null

export function getClientPendo() {
  if (!isClient()) {
    return null
  }

  return window.pendo
}

export function isClient() {
  return typeof window !== 'undefined'
}

export function reportPendoError(error: unknown) {
  datadogRum.addError(error, {
    tags: ['pendo'],
  })
}

function reportTrackEventDropped(reason: 'queue_full' | 'expired' | 'aborted', context: Record<string, unknown>) {
  datadogRum.addAction('pendo_track_event_dropped', { reason, ...context })
}

type TrackAgentState = 'absent' | 'stub' | 'not_ready' | 'ready'

function getTrackAgentState(): TrackAgentState {
  const pendo = getClientPendo()

  if (!pendo) {
    return 'absent'
  }

  if (typeof pendo.isReady !== 'function') {
    return 'stub'
  }

  try {
    return pendo.isReady() ? 'ready' : 'not_ready'
  }
  // a throwing isReady means readiness is unknown, not confirmed ready
  catch {
    return 'not_ready'
  }
}

export function sendTrackEvent(pendo: Pendo, event: PendingTrackEvent) {
  try {
    pendo.track(event.eventName, event.metadata)
    event.resolve(true)
  }
  catch (error) {
    reportPendoError(error)
    event.resolve(false)
  }
}

export function canTrackImmediately(pendo: Pendo) {
  if (typeof pendo.track !== 'function') {
    return false
  }

  // The stub accepts `track` into its own queue, so an event handed to it counts as delivered even if the agent never loads.
  if (typeof pendo.isReady !== 'function') {
    return false
  }

  return pendo.isReady()
}

function queueTrackEvent(event: PendingTrackEvent) {
  pendingTrackEvents.push(event)

  if (pendingTrackEvents.length > MAX_PENDING_TRACK_EVENTS) {
    const evicted = pendingTrackEvents.shift()

    if (evicted) {
      reportTrackEventDropped('queue_full', { eventName: evicted.eventName, queueLength: pendingTrackEvents.length })
      evicted.resolve(false)
    }
  }
}

function dropExpiredTrackEvents(now = Date.now()) {
  let agent: TrackAgentState | undefined

  for (let index = pendingTrackEvents.length - 1; index >= 0; index -= 1) {
    const event = pendingTrackEvents[index]!

    if (now - event.queuedAt >= MAX_TRACK_EVENT_AGE_MS) {
      pendingTrackEvents.splice(index, 1)
      agent ??= getTrackAgentState()
      reportTrackEventDropped('expired', { eventName: event.eventName, ageMs: now - event.queuedAt, agent })
      event.resolve(false)
    }
  }
}

export function queueAndScheduleTrackEvent(event: PendingTrackEvent) {
  queueTrackEvent(event)
  scheduleTrackFlush()
}

function clearTrackFlushTimer() {
  if (trackFlushTimer) {
    clearTimeout(trackFlushTimer)
    trackFlushTimer = null
  }
}

export function flushPendingTrackEvents(pendo: Pendo) {
  dropExpiredTrackEvents()
  const events = pendingTrackEvents.splice(0)

  for (const event of events) {
    sendTrackEvent(pendo, event)
  }

  clearTrackFlushTimer()
}

function stopTrackRetries() {
  const discarded = pendingTrackEvents.splice(0)
  clearTrackFlushTimer()
  reportTrackEventDropped('aborted', { discardedCount: discarded.length })
  discarded.forEach(event => event.resolve(false))
}

function retryPendingTrackEvents() {
  trackFlushTimer = null
  dropExpiredTrackEvents()

  if (!pendingTrackEvents.length) {
    return
  }

  const pendo = getClientPendo()

  try {
    if (pendo && canTrackImmediately(pendo)) {
      flushPendingTrackEvents(pendo)
      return
    }
  }
  catch (error) {
    reportPendoError(error)
    stopTrackRetries()
    return
  }

  scheduleTrackFlush()
}

function scheduleTrackFlush() {
  if (!pendingTrackEvents.length || trackFlushTimer) {
    return
  }

  trackFlushTimer = setTimeout(retryPendingTrackEvents, TRACK_FLUSH_INTERVAL_MS)
}

// queue and timer are module state that persists between tests in one file, so each test must reset them
export function resetTrackQueue() {
  const discarded = pendingTrackEvents.splice(0)
  clearTrackFlushTimer()
  discarded.forEach(event => event.resolve(false))
}
